import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import {
  FiUpload,
  FiImage,
  FiTrash2,
  FiCheck,
  FiSearch,
  FiLoader,
} from "react-icons/fi";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

type MediaItem = {
  id: string;
  filename: string;
  originalFilename: string;
  url: string;
  fileType: string;
  fileSize: number;
  alt: string | null;
  folder: string;
  createdAt: string;
};

interface MediaPickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (url: string, alt?: string) => void;
  folder?: "campaigns" | "blog" | "general" | "all";
  title?: string;
}

/** Extract relative path from media URL for local display */
function toDisplayUrl(url: string): string {
  try {
    const parsed = new URL(url);
    return parsed.pathname; // e.g. /api/media/general/...
  } catch {
    return url;
  }
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
}

export function MediaPicker({ open, onOpenChange, onSelect, folder = "all", title = "Choose an Image" }: MediaPickerProps) {
  const [items, setItems] = useState<MediaItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [uploadState, setUploadState] = useState<{ uploading: boolean; fileName: string; previewUrl: string | null }>({ uploading: false, fileName: "", previewUrl: null });
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [activeFolder, setActiveFolder] = useState(folder);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [failedUrls, setFailedUrls] = useState<Set<string>>(new Set());
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchMedia = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = activeFolder !== "all" ? `?folder=${activeFolder}` : "";
      const res = await fetch(`/api/admin/media${params}`, { credentials: "include" });
      if (res.ok) {
        const data = (await res.json()) as MediaItem[];
        setItems(data);
      }
    } catch {
      console.error("Failed to fetch media");
    } finally {
      setIsLoading(false);
    }
  }, [activeFolder]);

  useEffect(() => {
    if (open) {
      fetchMedia();
      setSelectedId(null);
      setFailedUrls(new Set());
    }
  }, [open, fetchMedia]);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowed = ["image/jpeg", "image/png", "image/gif", "image/webp", "image/svg+xml"];
    if (!allowed.includes(file.type)) {
      toast.error("Only image files are allowed (JPG, PNG, GIF, WebP, SVG)");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File must be under 5MB");
      return;
    }

    // Create local preview immediately
    const previewUrl = URL.createObjectURL(file);
    setUploadState({ uploading: true, fileName: file.name, previewUrl });

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("folder", activeFolder === "all" ? "general" : activeFolder);

      const res = await fetch("/api/admin/media/upload", {
        method: "POST",
        credentials: "include",
        body: formData,
      });

      if (res.ok) {
        const result = (await res.json()) as { data: MediaItem };
        // Add the new item to the front of the list immediately
        if (result.data) {
          setItems((prev) => [result.data, ...prev]);
          setSelectedId(result.data.id);
        } else {
          fetchMedia();
        }
        toast.success("Image uploaded");
      } else {
        const data = (await res.json()) as { error?: string };
        toast.error(data.error || "Upload failed");
      }
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploadState({ uploading: false, fileName: "", previewUrl: null });
      URL.revokeObjectURL(previewUrl);
      e.target.value = "";
    }
  };

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    try {
      const res = await fetch(`/api/admin/media/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (res.ok) {
        toast.success("Image deleted");
        setItems((prev) => prev.filter((m) => m.id !== id));
        if (selectedId === id) setSelectedId(null);
      }
    } catch {
      toast.error("Failed to delete");
    } finally {
      setDeletingId(null);
    }
  };

  const handleConfirm = () => {
    const item = items.find((m) => m.id === selectedId);
    if (item) {
      onSelect(item.url, item.alt || undefined);
      onOpenChange(false);
    }
  };

  const filtered = items.filter(
    (m) =>
      !searchQuery ||
      m.originalFilename.toLowerCase().includes(searchQuery.toLowerCase()) ||
      m.alt?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const folders = [
    { value: "all", label: "All" },
    { value: "campaigns", label: "Campaigns" },
    { value: "blog", label: "Blog" },
    { value: "general", label: "General" },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="!w-[90vw] !max-w-[900px] !h-[80vh] !max-h-[80vh] !p-0 !gap-0 flex flex-col">
        <DialogHeader className="shrink-0 px-5 py-4 border-b">
          <DialogTitle className="flex items-center gap-2">
            <FiImage className="w-5 h-5" />
            {title}
          </DialogTitle>
        </DialogHeader>

        {/* Toolbar */}
        <div className="shrink-0 px-5 py-3 border-b flex items-center gap-3 flex-wrap">
          <label className="cursor-pointer shrink-0">
            <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleUpload} disabled={uploadState.uploading} />
            <Button asChild variant="outline" size="sm" disabled={uploadState.uploading}>
              <span>
                {uploadState.uploading ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiUpload className="w-4 h-4" />}
                {uploadState.uploading ? "Uploading..." : "Upload Image"}
              </span>
            </Button>
          </label>
          <div className="w-px h-6 bg-slate-200 hidden sm:block" />
          <div className="relative flex-1 min-w-[180px] max-w-xs">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search images..."
              className="pl-9 h-9 placeholder:italic placeholder:text-slate-300"
            />
          </div>
          <div className="flex gap-1">
            {folders.map((f) => (
              <button
                key={f.value}
                onClick={() => setActiveFolder(f.value as typeof activeFolder)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  activeFolder === f.value
                    ? "bg-slate-900 text-white"
                    : "text-slate-500 hover:bg-slate-100"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* Grid */}
        <div className="flex-1 overflow-y-auto p-5">
          {isLoading ? (
            <div className="flex items-center justify-center h-40">
              <FiLoader className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          ) : filtered.length === 0 && !uploadState.uploading ? (
            <div className="text-center py-16">
              <FiImage className="w-12 h-12 mx-auto text-slate-200 mb-3" />
              <p className="text-sm text-slate-500">
                {searchQuery ? "No images match your search" : "No images uploaded yet"}
              </p>
              <p className="text-xs text-slate-400 mt-1">Upload an image to get started</p>
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
              {/* Upload progress card */}
              {uploadState.uploading && (
                <div className="relative aspect-square rounded-lg overflow-hidden border-2 border-primary/30 bg-slate-50">
                  {uploadState.previewUrl && (
                    <img src={uploadState.previewUrl} alt="Uploading" className="w-full h-full object-cover opacity-40" />
                  )}
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/60">
                    <FiLoader className="w-6 h-6 animate-spin text-primary mb-2" />
                    <p className="text-[11px] font-medium text-primary">Uploading...</p>
                    <p className="text-[10px] text-slate-500 truncate max-w-[90%] mt-0.5">{uploadState.fileName}</p>
                  </div>
                </div>
              )}

              {filtered.map((item) => {
                const imgFailed = failedUrls.has(item.id);
                return (
                  <div
                    key={item.id}
                    onClick={() => setSelectedId(item.id)}
                    className={`group relative aspect-square rounded-lg overflow-hidden border-2 cursor-pointer transition-all ${
                      selectedId === item.id
                        ? "border-primary ring-2 ring-primary/20"
                        : "border-transparent hover:border-slate-300"
                    }`}
                  >
                    {imgFailed ? (
                      <div className="w-full h-full bg-slate-100 flex flex-col items-center justify-center">
                        <FiImage className="w-6 h-6 text-slate-300 mb-1" />
                        <p className="text-[10px] text-slate-400 truncate max-w-[90%] px-1">{item.originalFilename}</p>
                      </div>
                    ) : (
                      <img
                        src={toDisplayUrl(item.url)}
                        alt={item.alt || item.originalFilename}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        onError={() => setFailedUrls((prev) => new Set(prev).add(item.id))}
                      />
                    )}
                    {selectedId === item.id && (
                      <div className="absolute top-2 left-2 w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                        <FiCheck className="w-4 h-4 text-white" />
                      </div>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(item.id);
                      }}
                      disabled={deletingId === item.id}
                      className="absolute top-2 right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      {deletingId === item.id ? (
                        <FiLoader className="w-3 h-3 text-white animate-spin" />
                      ) : (
                        <FiTrash2 className="w-3 h-3 text-white" />
                      )}
                    </button>
                    <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/60 to-transparent px-2 py-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                      <p className="text-[10px] text-white truncate">{item.originalFilename}</p>
                      <p className="text-[9px] text-white/70">{formatSize(item.fileSize)}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="shrink-0 px-5 py-3 border-t bg-slate-50 flex items-center justify-between">
          <p className="text-xs text-slate-400">
            {filtered.length} image{filtered.length !== 1 ? "s" : ""}
            {selectedId && " — 1 selected"}
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button size="sm" onClick={handleConfirm} disabled={!selectedId}>
              <FiCheck className="w-4 h-4 mr-1" />
              Use Image
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
